import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { LifeBuoy, Paperclip, X, CheckCircle2, ArrowRight, ArrowLeft, ImagePlus, Loader2, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';

const IMPACT_OPTIONS = [
    { value: 'cannot_continue', label: 'I cannot continue my work', hint: 'The system is down or blocking me completely.' },
    { value: 'major_delay',     label: 'It is causing a major delay', hint: 'A key process is affected and slowing things down a lot.' },
    { value: 'workaround',      label: 'I can continue using a workaround', hint: "There's a manual way around it for now." },
    { value: 'minor',           label: 'It is a minor issue or improvement', hint: 'Cosmetic, or a nice-to-have change.' },
];

const STEPS = [
    { key: 'whats_happening', label: "What's happening" },
    { key: 'how_urgent', label: 'How urgent' },
    { key: 'who_are_you', label: 'Who are you' },
    { key: 'anything_else', label: 'Anything to add' },
];

function detectEnvironment() {
    const ua = navigator.userAgent || '';
    let browser = 'Unknown browser';
    if (/Edg\//.test(ua)) browser = 'Edge';
    else if (/OPR\//.test(ua)) browser = 'Opera';
    else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = 'Chrome';
    else if (/Firefox\//.test(ua)) browser = 'Firefox';
    else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
    let os = 'Unknown OS';
    if (/Windows/.test(ua)) os = 'Windows';
    else if (/Mac OS X/.test(ua)) os = 'macOS';
    else if (/Android/.test(ua)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
    else if (/Linux/.test(ua)) os = 'Linux';
    const device_type = /iPad|Tablet/.test(ua) ? 'Tablet' : (/Mobi|Android|iPhone/.test(ua) ? 'Mobile' : 'Desktop');
    const screen_size = (window.screen && window.screen.width) ? `${window.screen.width}x${window.screen.height}` : null;
    return { browser, os, device_type, screen_size };
}

function isValidEmail(v) { return /\S+@\S+\.\S+/.test(v || ''); }

export default function SupportTicketForm() {
    const { code } = useParams();
    const [searchParams] = useSearchParams();
    const fromPageUrl = searchParams.get('from');
    const [platform, setPlatform] = useState(null);
    const [err, setErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [files, setFiles] = useState([]);
    const [step, setStep] = useState(0);
    const [identifying, setIdentifying] = useState(false);
    const [identified, setIdentified] = useState(null); // { contact_id, name, client_name } | null
    const [identifyChecked, setIdentifyChecked] = useState(false);
    const dropRef = useRef(null);

    const [form, setForm] = useState({
        request_type: 'issue',
        title: '',
        description: '',
        location_area: '',
        what_happened: '',
        what_expected: '',
        impact: '',
        email: '',
        other_name: '',
        other_org: '',
        extra_info: '',
    });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/public/support/platform/${code}`);
            setPlatform(r.data.platform);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || "This support link isn't recognised.");
        }
    }, [code]);
    useEffect(() => { load(); }, [load]);

    const accent = platform?.accent_color || '#0F5F6B';

    const addFiles = useCallback((list) => {
        const arr = Array.from(list || []).filter(f => f.size <= 15 * 1024 * 1024);
        if (arr.length !== (list || []).length) toast.error('Some files exceed the 15MB limit and were skipped.');
        if (arr.length) setFiles(prev => [...prev, ...arr]);
    }, []);

    // Global paste-to-attach — works from anywhere on the page (e.g. after using
    // Windows Snipping Tool / Cmd+Shift+4, just press Ctrl+V / Cmd+V), not just
    // inside a specific textarea.
    useEffect(() => {
        const handler = (e) => {
            const items = e.clipboardData?.items || [];
            const pasted = [];
            for (const it of items) {
                if (it.type?.startsWith('image/')) {
                    const f = it.getAsFile();
                    if (f) pasted.push(new File([f], f.name || `pasted-screenshot-${Date.now()}.png`, { type: f.type }));
                }
            }
            if (pasted.length) { addFiles(pasted); toast.message('Screenshot pasted'); setStep(3); }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [addFiles]);

    const runIdentify = async () => {
        if (!isValidEmail(form.email)) return;
        setIdentifying(true);
        try {
            const r = await api.post(`/public/support/platform/${code}/identify`, { email: form.email.trim() });
            setIdentified(r.data.contact_id ? r.data : null);
            if (!r.data.contact_id && r.data.recognized) {
                setForm(f => ({ ...f, other_name: r.data.name || f.other_name, other_org: r.data.org || f.other_org }));
                toast.message(`Welcome back, ${r.data.name} — we've filled in your details from last time.`);
            }
        } catch { setIdentified(null); }
        finally { setIdentifying(false); setIdentifyChecked(true); }
    };

    const stepValid = () => {
        const isIssue = form.request_type === 'issue';
        if (step === 0) {
            if (!form.title.trim()) return false;
            if (isIssue) return !!(form.location_area.trim() && form.what_happened.trim() && form.what_expected.trim());
            return !!form.description.trim();
        }
        if (step === 1) return !!form.impact;
        if (step === 2) {
            if (!isValidEmail(form.email)) return false;
            if (identified) return true;
            return identifyChecked && !!form.other_name.trim();
        }
        return true;
    };

    const next = async () => {
        if (step === 2 && !identifyChecked) { await runIdentify(); return; }
        if (!stepValid()) {
            const msgs = {
                0: form.request_type === 'issue' ? 'Please fill in the title and answer both questions below.' : 'Please fill in the title and description.',
                1: 'Please tell us how this is affecting your work.',
                2: identifyChecked ? 'Please enter your name.' : 'Please enter a valid email address.',
            };
            return toast.error(msgs[step] || 'Please complete this step.');
        }
        setStep(s => Math.min(s + 1, STEPS.length - 1));
    };
    const back = () => setStep(s => Math.max(s - 1, 0));

    const submit = async () => {
        setSubmitting(true);
        try {
            const env = detectEnvironment();
            const isIssue = form.request_type === 'issue';
            const payload = {
                platform: code,
                request_type: form.request_type,
                title: form.title.trim(),
                location_area: form.location_area.trim() || null,
                impact: form.impact,
                extra_info: form.extra_info || null,
                page_url: fromPageUrl || document.referrer || null,
                browser: env.browser, os: env.os, device_type: env.device_type, screen_size: env.screen_size,
            };
            if (isIssue) {
                payload.what_happened = form.what_happened.trim();
                payload.what_expected = form.what_expected.trim();
            } else {
                payload.description = form.description.trim();
            }
            if (identified?.contact_id) payload.contact_id = identified.contact_id;
            else {
                payload.submitted_by_name = form.other_name.trim();
                payload.submitted_by_email = form.email.trim();
                payload.submitted_by_org = form.other_org.trim() || null;
            }
            const r = await api.post('/public/support/tickets', payload);
            if (files.length) {
                const fd = new FormData();
                files.forEach(f => fd.append('files', f));
                await api.post(`/public/support/tickets/${r.data.public_token}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            setResult(r.data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Submission failed');
        } finally { setSubmitting(false); }
    };

    if (err) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="support-form-error">
                <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md text-center shadow-sm">
                    <LifeBuoy className="w-8 h-8 mx-auto text-slate-400" />
                    <div className="mt-3 font-semibold text-slate-800">{err}</div>
                </div>
            </div>
        );
    }
    if (!platform) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

    if (result) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="support-form-confirmation">
                <div className="bg-white rounded-xl border border-emerald-200 p-8 max-w-md text-center shadow-sm">
                    <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                    <h1 className="mt-3 text-[18px] font-bold text-slate-900">Thank you — your request has been submitted</h1>
                    <p className="mt-2 text-[13.5px] text-slate-600">
                        We will review it and keep you updated through the Support Hub. Your reference is:
                    </p>
                    <div className="mt-3 inline-block bg-slate-100 rounded-lg px-4 py-2 font-mono font-semibold" style={{ color: accent }} data-testid="support-ticket-reference">
                        {result.ticket_number}
                    </div>
                    <div className="mt-5 flex items-center justify-center gap-4">
                        <Link to={`/support/${result.public_token}`} className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold hover:underline" style={{ color: accent }} data-testid="support-view-request-link">
                            View your request <ArrowRight size={14} />
                        </Link>
                        <Link to="/support/my-requests" className="inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:underline" data-testid="support-view-all-requests-link">
                            <ListChecks size={13} /> See all your requests
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const isIssue = form.request_type === 'issue';

    return (
        <div className="min-h-screen bg-slate-50" data-testid="support-ticket-form" style={{ '--bm-primary': accent, '--bm-accent': accent }}>
            <header className="text-white px-6 py-5" style={{ background: accent }}>
                <div className="max-w-2xl mx-auto flex items-center gap-2">
                    <LifeBuoy className="w-5 h-5 text-white/80" />
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-white/60">{platform.name} Support</div>
                        <div className="text-[16px] font-semibold">Report an Issue or Request an Improvement</div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {fromPageUrl && (
                    <div className="mb-4 text-[11.5px] text-slate-500 bg-white border border-slate-200 rounded-md px-3 py-2" data-testid="support-from-page-note">
                        We've noted the page you came from to help us look into this faster.
                    </div>
                )}

                {/* Step progress */}
                <div className="flex items-center gap-1.5 mb-5" data-testid="support-step-progress">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.key}>
                            <div className={`flex items-center gap-1.5 ${i <= step ? '' : 'opacity-40'}`}>
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                                     style={{ background: i <= step ? accent : '#cbd5e1' }}>{i + 1}</div>
                                <span className="text-[11.5px] font-medium text-slate-700 hidden sm:inline">{s.label}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
                        </React.Fragment>
                    ))}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-5">
                    {step === 0 && (
                        <>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">What would you like to report? <span className="text-rose-500">*</span></label>
                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        { v: 'issue', l: 'Report an Issue', d: 'Something is not working correctly, is showing incorrect information, or is preventing you from completing part of your work.' },
                                        { v: 'improvement', l: 'Request an Improvement', d: 'You would like an existing feature improved, changed or made easier to use. Your normal work can continue while the request is reviewed.' },
                                    ].map(opt => (
                                        <button type="button" key={opt.v} onClick={() => set('request_type', opt.v)}
                                                data-testid={`support-type-${opt.v}`}
                                                className={`text-left rounded-lg border p-3 transition ${form.request_type === opt.v ? 'bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                                                style={form.request_type === opt.v ? { borderColor: accent } : {}}>
                                            <div className="text-[13.5px] font-semibold text-slate-800">{opt.l}</div>
                                            <div className="text-[11.5px] text-slate-500 mt-0.5">{opt.d}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Request title <span className="text-rose-500">*</span></label>
                                <input value={form.title} onChange={e => set('title', e.target.value)}
                                       placeholder="Short summary of the problem or request" maxLength={200}
                                       className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                       data-testid="support-title-input" />
                            </div>

                            {isIssue ? (
                                <>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Where in the platform is this happening? <span className="text-rose-500">*</span></label>
                                        <input value={form.location_area} onChange={e => set('location_area', e.target.value)}
                                               placeholder="e.g. Invoices page, the client dashboard, the checkout screen…" maxLength={200}
                                               className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                               data-testid="support-location-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">What were you doing, and what happened? <span className="text-rose-500">*</span></label>
                                        <textarea value={form.what_happened} onChange={e => set('what_happened', e.target.value)}
                                                  placeholder="e.g. I was trying to save a new invoice, and got an error message with the page going blank…" rows={3}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="support-happened-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">What did you expect to happen? <span className="text-rose-500">*</span></label>
                                        <textarea value={form.what_expected} onChange={e => set('what_expected', e.target.value)}
                                                  placeholder="e.g. The invoice should have saved and shown in the list…" rows={2}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="support-expected-input" />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Where in the platform does this relate to? <span className="text-slate-400 font-normal">(optional)</span></label>
                                        <input value={form.location_area} onChange={e => set('location_area', e.target.value)}
                                               placeholder="e.g. Invoices page, the client dashboard…" maxLength={200}
                                               className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                               data-testid="support-location-input" />
                                    </div>
                                    <div>
                                        <label className="text-[13px] font-semibold text-slate-800">Tell us what you would like changed <span className="text-rose-500">*</span></label>
                                        <textarea value={form.description} onChange={e => set('description', e.target.value)}
                                                  placeholder="What would you like changed or improved, and why would this help you?" rows={5}
                                                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                                  data-testid="support-description-input" />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {step === 1 && (
                        <div>
                            <label className="text-[13px] font-semibold text-slate-800">How is this affecting your work? <span className="text-rose-500">*</span></label>
                            <div className="mt-2 space-y-2">
                                {IMPACT_OPTIONS.map(opt => (
                                    <label key={opt.value} className={`flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer transition ${form.impact === opt.value ? 'bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
                                           style={form.impact === opt.value ? { borderColor: accent } : {}}
                                           data-testid={`support-impact-${opt.value}`}>
                                        <input type="radio" name="impact" className="mt-0.5" checked={form.impact === opt.value} onChange={() => set('impact', opt.value)} />
                                        <div>
                                            <div className="text-[13px] font-medium text-slate-800">{opt.label}</div>
                                            <div className="text-[11px] text-slate-500">{opt.hint}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <label className="text-[13px] font-semibold text-slate-800">Your email <span className="text-rose-500">*</span></label>
                            <input value={form.email} type="email" onChange={e => { set('email', e.target.value); setIdentifyChecked(false); setIdentified(null); }}
                                   onBlur={runIdentify} placeholder="you@example.com"
                                   className="mt-1.5 w-full h-11 rounded-md border border-slate-300 px-3 text-[13.5px]"
                                   data-testid="support-email-input" />
                            {identifying && <div className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-400"><Loader2 size={13} className="animate-spin" /> Checking…</div>}
                            {!identifying && identifyChecked && identified && (
                                <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-800" data-testid="support-identified-chip">
                                    <span>You're logged in as <strong>{identified.name}</strong> ({identified.client_name})</span>
                                    <button type="button" onClick={() => { setIdentified(null); setIdentifyChecked(false); }} className="text-emerald-600 hover:underline shrink-0 ml-2" data-testid="support-not-you-btn">Not you?</button>
                                </div>
                            )}
                            {!identifying && identifyChecked && !identified && isValidEmail(form.email) && (
                                <div className="mt-3 space-y-2" data-testid="support-manual-identity-fields">
                                    <div className="text-[11.5px] text-slate-500">We don't recognise that email yet — please tell us a bit more:</div>
                                    <input value={form.other_name} onChange={e => set('other_name', e.target.value)} placeholder="Your name *"
                                           className="w-full h-10 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="support-other-name" />
                                    <input value={form.other_org} onChange={e => set('other_org', e.target.value)} placeholder="Your organisation (optional)"
                                           className="w-full h-10 rounded-md border border-slate-300 px-3 text-[13.5px]" data-testid="support-other-org" />
                                </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Screenshots or attachments <span className="text-slate-400 font-normal">(optional)</span></label>
                                <div
                                    ref={dropRef}
                                    onDragOver={e => { e.preventDefault(); }}
                                    onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                                    onClick={() => document.getElementById('support-file-input').click()}
                                    className="mt-1.5 border-2 border-dashed border-slate-300 rounded-lg p-5 text-center cursor-pointer hover:border-slate-400 transition"
                                    data-testid="support-dropzone"
                                >
                                    <ImagePlus className="w-5 h-5 mx-auto text-slate-400" />
                                    <div className="text-[11.5px] text-slate-500 mt-1">Tap to add a photo, or drag & drop a file here.</div>
                                    <div className="text-[10.5px] text-slate-400 mt-1">Used the Snipping Tool (Windows) or Cmd+Shift+4 (Mac)? Just press <strong>Ctrl+V</strong> (or <strong>Cmd+V</strong>) anywhere on this page to paste it in.</div>
                                    <input id="support-file-input" type="file" multiple accept="image/*,.pdf" className="hidden" onChange={e => addFiles(e.target.files)} data-testid="support-file-input" />
                                </div>
                                {files.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        {files.map((f, i) => (
                                            <div key={i} className="flex items-center justify-between text-[12px] bg-slate-50 rounded px-2.5 py-1.5" data-testid={`support-file-${i}`}>
                                                <span className="flex items-center gap-1.5 text-slate-700 truncate"><Paperclip size={12} />{f.name}</span>
                                                <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-500"><X size={13} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[13px] font-semibold text-slate-800">Anything else we should know? <span className="text-slate-400 font-normal">(optional)</span></label>
                                <textarea value={form.extra_info} onChange={e => set('extra_info', e.target.value)}
                                          placeholder="Deadlines, workarounds, number of users affected, additional context…" rows={2}
                                          className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2 text-[13.5px]"
                                          data-testid="support-extra-info" />
                            </div>
                            <div className="text-[11px] text-slate-500 bg-slate-50 rounded-md p-2.5">
                                Critical and high-impact issues will be reviewed as soon as possible. We'll keep you updated on where things stand via the tracking link we send you.
                            </div>
                        </>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                        {step > 0 && (
                            <button type="button" onClick={back} className="h-11 px-4 rounded-md font-semibold text-[13.5px] border border-slate-300 text-slate-600 flex items-center gap-1.5" data-testid="support-back-btn">
                                <ArrowLeft size={14} /> Back
                            </button>
                        )}
                        {step < STEPS.length - 1 ? (
                            <button type="button" onClick={next} disabled={identifying}
                                    className="flex-1 h-11 rounded-md font-semibold text-white text-[14px] disabled:opacity-60 flex items-center justify-center gap-1.5"
                                    style={{ background: accent }} data-testid="support-next-btn">
                                Continue <ArrowRight size={14} />
                            </button>
                        ) : (
                            <button type="button" onClick={submit} disabled={submitting}
                                    className="flex-1 h-11 rounded-md font-semibold text-white text-[14px] disabled:opacity-60"
                                    style={{ background: accent }} data-testid="support-submit-btn">
                                {submitting ? 'Submitting…' : 'Submit request'}
                            </button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
